import type { FormEvent } from 'react'
import { useCallback, useState } from 'react'
import {
  getSunoPromptGroups,
  getSunoPrompts,
  type SunoPrompt,
  type SunoPromptFilters,
  type SunoPromptGroup
} from '../../../api/suno'
import { usePromptLibraryEditorActions } from './usePromptLibraryEditorActions'

type PromptLibraryDataOptions = {
  toast: (message: string) => void
}

export const usePromptLibraryData = ({ toast }: PromptLibraryDataOptions) => {
  const [promptSearch, setPromptSearch] = useState('')
  const [promptGroupFilter, setPromptGroupFilter] = useState('')
  const [promptSortBy, setPromptSortBy] = useState<SunoPromptFilters['sortBy']>('updated_at')
  const [promptSortDirection, setPromptSortDirection] = useState<SunoPromptFilters['sortDirection']>('desc')
  const [promptPage, setPromptPage] = useState(1)
  const [promptPageSize, setPromptPageSize] = useState(25)
  const [promptTotal, setPromptTotal] = useState(0)
  const [sunoPrompts, setSunoPrompts] = useState<SunoPrompt[]>([])
  const [promptsLoading, setPromptsLoading] = useState(false)
  const [promptsError, setPromptsError] = useState<string | null>(null)
  const [sunoPromptGroups, setSunoPromptGroups] = useState<SunoPromptGroup[]>([])

  const loadSunoPrompts = useCallback(async (
    search = promptSearch,
    group = promptGroupFilter,
    page = promptPage,
    pageSize = promptPageSize,
    sortBy = promptSortBy,
    sortDirection = promptSortDirection
  ): Promise<void> => {
    setPromptsLoading(true)
    setPromptsError(null)

    try {
      const result = await getSunoPrompts({
        group: group || undefined,
        page,
        pageSize,
        search,
        sortBy,
        sortDirection
      })
      setSunoPrompts(result.items)
      setPromptTotal(result.total)
    } catch (requestError) {
      setPromptsError(requestError instanceof Error ? requestError.message : 'Unable to load prompts.')
    } finally {
      setPromptsLoading(false)
    }
  }, [promptGroupFilter, promptPage, promptPageSize, promptSearch, promptSortBy, promptSortDirection])

  const loadSunoPromptGroups = useCallback(async (search?: string): Promise<void> => {
    try {
      setSunoPromptGroups(await getSunoPromptGroups(search))
    } catch {
      // Group loading errors are non-blocking for the prompt list.
    }
  }, [])

  const togglePromptSort = (sortBy: NonNullable<SunoPromptFilters['sortBy']>): void => {
    const nextDirection = promptSortBy === sortBy && promptSortDirection === 'asc' ? 'desc' : 'asc'
    setPromptSortBy(sortBy)
    setPromptSortDirection(nextDirection)
    setPromptPage(1)
    void loadSunoPrompts(promptSearch, promptGroupFilter, 1, promptPageSize, sortBy, nextDirection)
  }

  const updatePromptPage = (page: number): void => {
    setPromptPage(page)
    void loadSunoPrompts(promptSearch, promptGroupFilter, page, promptPageSize, promptSortBy, promptSortDirection)
  }

  const updatePromptPageSize = (pageSize: number): void => {
    setPromptPageSize(pageSize)
    setPromptPage(1)
    void loadSunoPrompts(promptSearch, promptGroupFilter, 1, pageSize, promptSortBy, promptSortDirection)
  }

  const clearPromptSearch = (): void => {
    setPromptSearch('')
    setPromptPage(1)
    void loadSunoPrompts('', promptGroupFilter, 1)
  }

  const selectPromptGroup = (groupId: string): void => {
    setPromptGroupFilter(groupId)
    setPromptPage(1)
    void loadSunoPrompts(promptSearch, groupId, 1)
  }

  const submitPromptSearch = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault()
    setPromptPage(1)
    void loadSunoPrompts(promptSearch, promptGroupFilter, 1)
  }

  const {
    promptDeleting,
    promptSaving,
    confirmDeletePrompt,
    savePrompt,
    suggestPromptMetadata
  } = usePromptLibraryEditorActions({
    onPromptGroupsChanged: loadSunoPromptGroups,
    onPromptsChanged: loadSunoPrompts,
    toast
  })

  return {
    promptSearch,
    setPromptSearch,
    promptGroupFilter,
    promptSortBy,
    promptSortDirection,
    promptPage,
    promptPageSize,
    promptTotal,
    sunoPrompts,
    promptsLoading,
    promptsError,
    sunoPromptGroups,
    promptSaving,
    promptDeleting,
    clearPromptSearch,
    confirmDeletePrompt,
    loadSunoPromptGroups,
    loadSunoPrompts,
    savePrompt,
    selectPromptGroup,
    submitPromptSearch,
    suggestPromptMetadata,
    togglePromptSort,
    updatePromptPage,
    updatePromptPageSize
  }
}
